import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Button } from 'reactstrap';
import ButtonLoader from '../../../../components/Layout/Buttons/ButtonLoader';
import { LegalIssuesForm } from '../../../../components/LoiForm/LegalIssuesForm';
import { LoiForm } from '../../../../slices/marketplace/loi-form/loi-form';
import { LoiFormMode } from '../../../../slices/marketplace/loi-form/loi-form-mode.enum';
import {
    fetchLoiFormAdminTemplate,
    updateLoiForm,
} from '../../../../slices/marketplace/loi-form/loi-forms-api';
import createNotification from '../../../../utils/createNotification';
import { useStateSelector } from '../../../../store/selectors';
import Helpers from '../../../../utils/helper';
import ReactSwitch from 'react-switch';
import {
    LegalIssuesGridEditor,
    LegalIssuesGridEditorHandle,
} from './LegalIssuesGridEditor/LegalIssuesGridEditor';
import { LegalIssueType } from '../../../../slices/marketplace/loi-form/legal-issue-type.enum';
import './LegalIssuesGuidance.scss';

export const LegalIssuesGuidance = () => {
    const axios = useStateSelector((state) => state.core.axios);
    const [formData, setFormData] = useState<LoiForm | null>(null);
    const [originalFormData, setOriginalFormData] = useState<LoiForm | null>(
        null
    );
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [isEditMode, setIsEditMode] = useState(false);
    const gridEditorRef = useRef<LegalIssuesGridEditorHandle>(null);

    const loadForm = useCallback(async () => {
        if (!axios) return;
        setIsLoading(true);
        setError(null);
        try {
            const data = await fetchLoiFormAdminTemplate(axios);
            setFormData(data);
            setOriginalFormData(data);
        } catch {
            createNotification(
                'Could not load Legal Issues Guidance. Please try again.',
                'error'
            );
            setError('Admin template not found.');
        } finally {
            setIsLoading(false);
        }
    }, [axios]);

    useEffect(() => {
        loadForm();
    }, [loadForm]);

    const handleChange = useCallback((payload: LoiForm) => {
        setFormData((prev) =>
            prev
                ? {
                      ...prev,
                      legalIssueSections: payload.legalIssueSections,
                      legalIssues: payload.legalIssues,
                  }
                : null
        );
    }, []);

    const handleGridFormChange = useCallback((payload: LoiForm) => {
        setFormData(payload);
    }, []);

    const handleSaveAndClose = useCallback(async () => {
        if (!axios || !formData?.id) return;
        if (gridEditorRef.current && !gridEditorRef.current.validate()) {
            createNotification(
                'Please fix all validation errors before saving.',
                'error'
            );
            return;
        }
        setIsSaving(true);
        try {
            const payload: LoiForm = {
                ...formData,
                isAdminTemplate: formData.isAdminTemplate ?? true,
                termForm: formData.termForm ?? undefined,
                additionalCovenants: formData.additionalCovenants ?? [],
            };
            await updateLoiForm(axios, formData.id, payload);
            setIsEditMode(false);
            await loadForm();
        } catch {
            createNotification(
                'Could not save Legal Issues Guidance. Please try again.',
                'error'
            );
        } finally {
            setIsSaving(false);
        }
    }, [axios, formData, loadForm]);

    const handleCancelAndClose = useCallback(() => {
        setFormData(originalFormData);
        setIsEditMode(false);
    }, [originalFormData]);

    if (isLoading) {
        return (
            <div className="legal-issues-guidance">
                {Helpers.renderTableLoader()}
            </div>
        );
    }

    if (error || !formData) {
        return (
            <div className="legal-issues-guidance">
                <div className="text-danger">{error ?? 'No data'}</div>
            </div>
        );
    }

    return (
        <div className="legal-issues-guidance">
            <div
                role="tabpanel"
                id="panel-legal-issues-guidance"
                aria-labelledby="tab-legal-issues-guidance"
                aria-hidden="false">
                <div className="legal-issues-guidance-toolbar">
                    <div className="toolbar-left">
                        {isEditMode && (
                            <Button
                                color="primary"
                                size="sm"
                                onClick={() =>
                                    gridEditorRef.current?.addRow()
                                }>
                                + Add New Row
                            </Button>
                        )}
                    </div>
                    <div className="toolbar-right">
                        <strong>Design mode</strong>
                        <ReactSwitch
                            offColor="#d92550"
                            checked={isEditMode}
                            onChange={(val) => {
                                if (!val) {
                                    setFormData(originalFormData);
                                }
                                setIsEditMode(val);
                            }}
                            height={22}
                        />
                    </div>
                </div>
                <div className="card-body">
                    {isEditMode ? (
                        <LegalIssuesGridEditor
                            ref={gridEditorRef}
                            formData={formData}
                            onFormChange={handleGridFormChange}
                            legalIssueType={LegalIssueType.LegalIssue}
                        />
                    ) : (
                        <LegalIssuesForm
                            legalIssueSections={formData.legalIssueSections}
                            legalIssues={formData.legalIssues}
                            onChange={handleChange}
                            mode={LoiFormMode.AdminDefaults}
                        />
                    )}
                </div>
                {isEditMode && (
                    <div className="legal-issues-guidance-actions">
                        <Button
                            color=""
                            className="btn-cancel"
                            disabled={isSaving}
                            onClick={handleCancelAndClose}>
                            Cancel & Close
                        </Button>
                        <ButtonLoader
                            buttonText="Save & Close"
                            loaderButtonText=""
                            disabled={false}
                            isLoading={isSaving}
                            onClick={handleSaveAndClose}
                            className="btn btn-primary"
                        />
                    </div>
                )}
            </div>
        </div>
    );
};
